import { all, call, put, takeLatest, delay } from 'redux-saga/effects'
import ThumbnailActions, { ThumbnailTypes } from 'Redux/ThumbnailRedux'

// Regenerate Thumbnail Image
export function* regenerateThumbnail(api, action) {
  try {
    const { thumbnailId, prompt, callback } = action
    const params = prompt ? { prompt } : {}

    const resp = yield call(api.regenerateThumbnailImage, thumbnailId, params)

    if (resp.ok) {
      yield put(ThumbnailActions.thumbnailRegenerateSuccess(resp.data))
      if (callback) {
        callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to regenerate image'
      yield put(ThumbnailActions.thumbnailRegenerateFailure(error))
      if (callback) {
        callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to regenerate image'
    yield put(ThumbnailActions.thumbnailRegenerateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Thumbnail Status
export function* getThumbnailStatus(api, action) {
  try {
    const resp = yield call(api.getThumbnailImageStatus, action.thumbnailId)

    if (resp.ok) {
      yield put(ThumbnailActions.thumbnailStatusSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get image status'
      yield put(ThumbnailActions.thumbnailStatusFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get image status'
    yield put(ThumbnailActions.thumbnailStatusFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Thumbnail Until Complete
export function* pollThumbnail(api, action) {
  const { thumbnailId, interval: pollInterval = 3000, callback } = action
  const maxAttempts = 60 // 3 minutes max
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getThumbnailImageStatus, thumbnailId)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get image status'
        yield put(ThumbnailActions.thumbnailPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      
      if (status.status === 'completed') {
        yield put(ThumbnailActions.thumbnailPollSuccess(status))
        if (callback) {
          callback(status, null)
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Image regeneration failed'
        yield put(ThumbnailActions.thumbnailPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      attempts++
      yield delay(pollInterval)
    }
    
    // Timeout
    const error = 'Image regeneration timed out'
    yield put(ThumbnailActions.thumbnailPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(ThumbnailActions.thumbnailPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Get Thumbnail History
export function* getThumbnailHistory(api, action) {
  try {
    const resp = yield call(api.getThumbnailImageHistory, action.thumbnailId)

    if (resp.ok) {
      yield put(ThumbnailActions.thumbnailHistorySuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get image history'
      yield put(ThumbnailActions.thumbnailHistoryFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get image history'
    yield put(ThumbnailActions.thumbnailHistoryFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Select From History
export function* selectFromHistory(api, action) {
  try {
    const { thumbnailId, historyIndex, callback } = action
    const resp = yield call(api.selectThumbnailFromHistory, thumbnailId, { history_index: historyIndex })

    if (resp.ok) {
      yield put(ThumbnailActions.thumbnailSelectHistorySuccess(resp.data))
      if (callback) {
        callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to select from history'
      yield put(ThumbnailActions.thumbnailSelectHistoryFailure(error))
      if (callback) {
        callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to select from history'
    yield put(ThumbnailActions.thumbnailSelectHistoryFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// ============ Animation Sagas ============

// Generate Animation
export function* generateAnimation(api, action) {
  try {
    const { thumbnailId, animationPrompt, force, callback } = action
    const params = {}
    if (animationPrompt) {
      params.animation_prompt = animationPrompt
    }
    if (force) {
      params.force = true
    }

    const resp = yield call(api.generateAnimation, thumbnailId, params)

    if (resp.ok) {
      yield put(ThumbnailActions.animationGenerateSuccess(resp.data))
      if (callback) {
        callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to generate animation'
      yield put(ThumbnailActions.animationGenerateFailure(error))
      if (callback) {
        callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to generate animation'
    yield put(ThumbnailActions.animationGenerateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Animation Status
export function* getAnimationStatus(api, action) {
  try {
    const resp = yield call(api.getAnimationStatus, action.thumbnailId)

    if (resp.ok) {
      yield put(ThumbnailActions.animationStatusSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get animation status'
      yield put(ThumbnailActions.animationStatusFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get animation status'
    yield put(ThumbnailActions.animationStatusFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Animation Until Complete
export function* pollAnimation(api, action) {
  const { thumbnailId, interval: pollInterval = 3000, callback } = action
  const maxAttempts = 400 // ~20 minutes
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getAnimationStatus, thumbnailId)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get animation status'
        yield put(ThumbnailActions.animationPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      
      if (status.animation_status === 'completed') {
        yield put(ThumbnailActions.animationPollSuccess(status))
        if (callback) {
          callback(status, null)
        }
        return
      }
      
      if (status.animation_status === 'failed') {
        const error = status.animation_error_message || 'Animation generation failed'
        yield put(ThumbnailActions.animationPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      if (status.animation_status === 'skipped') {
        yield put(ThumbnailActions.animationPollSuccess(status))
        if (callback) {
          callback(status, null)
        }
        return
      }
      
      attempts++
      yield delay(pollInterval)
    }
    
    // Timeout
    const error = 'Animation generation timed out'
    yield put(ThumbnailActions.animationPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(ThumbnailActions.animationPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Regenerate Animation
export function* regenerateAnimation(api, action) {
  try {
    const resp = yield call(api.regenerateAnimation, action.thumbnailId)

    if (resp.ok) {
      yield put(ThumbnailActions.animationRegenerateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to regenerate animation'
      yield put(ThumbnailActions.animationRegenerateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to regenerate animation'
    yield put(ThumbnailActions.animationRegenerateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Skip Animation
export function* skipAnimation(api, action) {
  try {
    const resp = yield call(api.skipAnimation, action.thumbnailId)

    if (resp.ok) {
      yield put(ThumbnailActions.animationSkipSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to skip animation'
      yield put(ThumbnailActions.animationSkipFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to skip animation'
    yield put(ThumbnailActions.animationSkipFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// ============ Batch Animation Sagas ============

// Batch Generate Animations
export function* batchGenerateAnimations(api, action) {
  try {
    const resp = yield call(api.batchGenerateAnimations, action.scriptId)

    if (resp.ok) {
      yield put(ThumbnailActions.animationBatchGenerateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to batch generate animations'
      yield put(ThumbnailActions.animationBatchGenerateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to batch generate animations'
    yield put(ThumbnailActions.animationBatchGenerateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Batch Animation Status
export function* getBatchAnimationStatus(api, action) {
  try {
    const resp = yield call(api.getBatchAnimationStatus, action.scriptId)

    if (resp.ok) {
      yield put(ThumbnailActions.animationBatchStatusSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get batch animation status'
      yield put(ThumbnailActions.animationBatchStatusFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get batch animation status'
    yield put(ThumbnailActions.animationBatchStatusFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Batch Animation Until Complete
export function* pollBatchAnimation(api, action) {
  const { scriptId, interval: pollInterval = 3000, callback } = action
  const maxAttempts = 400 // ~20 minutes
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getBatchAnimationStatus, scriptId)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get batch animation status'
        yield put(ThumbnailActions.animationBatchPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      const { stats } = status
      const stillProcessing = stats.pending > 0 || stats.processing > 0
      
      if (!stillProcessing) {
        yield put(ThumbnailActions.animationBatchPollSuccess(status))
        if (callback) {
          callback(status, null)
        }
        return
      }
      
      // Continue polling - don't call callback for progress updates
      // Progress is tracked via Redux state
      attempts++
      yield delay(pollInterval)
    }
    
    // Timeout
    const error = 'Batch animation generation timed out'
    yield put(ThumbnailActions.animationBatchPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(ThumbnailActions.animationBatchPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Watcher saga
export default function* thumbnailSaga(api) {
  yield all([
    takeLatest(ThumbnailTypes.THUMBNAIL_REGENERATE_REQUEST, regenerateThumbnail, api),
    takeLatest(ThumbnailTypes.THUMBNAIL_STATUS_REQUEST, getThumbnailStatus, api),
    takeLatest(ThumbnailTypes.THUMBNAIL_POLL_REQUEST, pollThumbnail, api),
    takeLatest(ThumbnailTypes.THUMBNAIL_HISTORY_REQUEST, getThumbnailHistory, api),
    takeLatest(ThumbnailTypes.THUMBNAIL_SELECT_HISTORY_REQUEST, selectFromHistory, api),
    takeLatest(ThumbnailTypes.ANIMATION_GENERATE_REQUEST, generateAnimation, api),
    takeLatest(ThumbnailTypes.ANIMATION_STATUS_REQUEST, getAnimationStatus, api),
    takeLatest(ThumbnailTypes.ANIMATION_POLL_REQUEST, pollAnimation, api),
    takeLatest(ThumbnailTypes.ANIMATION_REGENERATE_REQUEST, regenerateAnimation, api),
    takeLatest(ThumbnailTypes.ANIMATION_SKIP_REQUEST, skipAnimation, api),
    takeLatest(ThumbnailTypes.ANIMATION_BATCH_GENERATE_REQUEST, batchGenerateAnimations, api),
    takeLatest(ThumbnailTypes.ANIMATION_BATCH_STATUS_REQUEST, getBatchAnimationStatus, api),
    takeLatest(ThumbnailTypes.ANIMATION_BATCH_POLL_REQUEST, pollBatchAnimation, api),
  ])
}
